// AB#1753 — Thin wrapper over the @microsoft/signalr HubConnection for the
// Calendar v1 realtime hub (C7 of Epic AB#1746). Mirrors the pattern used
// by NotificationsContext — separate connection at a different hub path
// so the two coexist cleanly.

import * as signalR from '@microsoft/signalr';
import { ensureValidAccessToken } from '../lib/auth/ensureValidAccessToken';
import { authAwareRetryPolicy } from '../lib/realtime/authAwareRetryPolicy';

/**
 * Derive the calendar-hub URL from the API base. The BE exposes the hub
 * at `/hubs/calendar` on the same host as the REST API; we strip a
 * trailing `/api` (if present) so both URL shapes (`{host}` and
 * `{host}/api`) resolve to the same hub origin.
 */
export function calendarHubUrl(): string {
    const base = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
    const host = base.replace(/\/api$/, '');
    return `${host}/hubs/calendar`;
}

/**
 * Build (but do not start) a SignalR connection to the calendar hub.
 * - `accessTokenFactory` reads from localStorage on every call, matching
 *   the pattern used by NotificationsContext — keeps SignalR in sync with
 *   axios's silent refresh.
 * - `withAutomaticReconnect` uses the default exponential backoff
 *   (0/2/10/30s) but stops on a 401 / 403 or once signed out (S1, AB#2204 —
 *   a revoked staff session must not loop on reconnects). On reconnect the BE auto-joins the same `business:{id}`
 *   group based on the JWT claim, so the client has nothing to do.
 */
export function buildCalendarConnection(): signalR.HubConnection {
    return new signalR.HubConnectionBuilder()
        .withUrl(calendarHubUrl(), {
            accessTokenFactory: () => ensureValidAccessToken(),
        })
        .withAutomaticReconnect(authAwareRetryPolicy)
        .build();
}
